import type { CardDefId } from '@cards/engine';
import type { DeckPassiveId } from '@cards/cards';
import type { SavedDeckDto } from '../decks/api.js';
import { parseDeckUuid, parseSavedDeckDto } from '../decks/api.js';

export type ChallengeStageNumber = 1 | 2 | 3;
export type ChallengeDifficulty = 'beginner' | 'casual' | 'hard';
export type ChallengeStageStatus = 'locked' | 'available' | 'completed';

export interface ChallengeStageDto {
  readonly stage: ChallengeStageNumber;
  readonly difficulty: ChallengeDifficulty;
  readonly title: string;
  readonly lesson: string;
  readonly passiveId: DeckPassiveId | null;
  readonly cardIds: readonly CardDefId[];
  readonly status: ChallengeStageStatus;
  /** Exact account-owned copy of this recipe, when one already exists. */
  readonly savedDeckId: string | null;
}

export type ChallengeRewardDto =
  | Readonly<{ status: 'locked' }>
  | Readonly<{ status: 'unlocked'; cardDefId: CardDefId }>;

export interface ChallengeTrackDto {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly gamePlan: readonly string[];
  readonly completedStages: number;
  readonly stages: readonly ChallengeStageDto[];
  readonly reward: ChallengeRewardDto;
}

export interface ChallengesProjection {
  readonly catalogVersion: number;
  readonly tracks: readonly ChallengeTrackDto[];
}

export interface ChallengeStartResult {
  readonly kind: 'created' | 'replayed';
  readonly roomId: string;
  readonly playerId: string;
  readonly reconnectToken: string;
}

export interface ChallengeStartOptions {
  /** UUID retained by callers that retry after a potentially lost acknowledgement. */
  readonly operationId?: string;
  /** 256-bit base64url bearer retained together with operationId across retries. */
  readonly bootstrapToken?: string;
  readonly signal?: AbortSignal;
}

export interface ChallengeDeckCopyOptions {
  /** UUID retained by callers that retry after a potentially lost acknowledgement. */
  readonly operationId?: string;
  readonly signal?: AbortSignal;
}

export interface ChallengeDeckCopyResult {
  readonly deck: SavedDeckDto;
  readonly outcome: 'created' | 'reused' | 'replayed';
}

export interface ChallengeApi {
  list(signal?: AbortSignal): Promise<ChallengesProjection>;
  start(
    trackId: string,
    stage: ChallengeStageNumber,
    csrfToken: string,
    options?: ChallengeStartOptions,
  ): Promise<ChallengeStartResult>;
  copyDeck(
    trackId: string,
    stage: ChallengeStageNumber,
    csrfToken: string,
    options?: ChallengeDeckCopyOptions,
  ): Promise<ChallengeDeckCopyResult>;
}

export type ChallengeApiErrorCode =
  | 'ACCOUNT_REQUIRED'
  | 'PROFILE_INCOMPLETE'
  | 'FORBIDDEN_ORIGIN'
  | 'CHALLENGE_NOT_FOUND'
  | 'CHALLENGE_STAGE_LOCKED'
  | 'OPERATION_ID_CONFLICT'
  | 'DECK_NOT_FOUND'
  | 'DECK_LIMIT_REACHED'
  | 'DECK_INVALID'
  | 'DECK_NAME_INVALID'
  | 'INVALID_REQUEST'
  | 'REQUEST_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'SERVICE_UNAVAILABLE'
  | 'INTERNAL_ERROR'
  | 'METHOD_NOT_ALLOWED'
  | 'NOT_FOUND'
  | 'INVALID_RESPONSE';

export class ChallengeApiError extends Error {
  override readonly name = 'ChallengeApiError';

  constructor(readonly code: ChallengeApiErrorCode, readonly status: number) {
    super(code);
  }
}

type JsonObject = Record<string, unknown>;

function strictObject(value: unknown, keys: readonly string[], label: string): JsonObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label}: expected object`);
  }
  const object = value as JsonObject;
  const actual = Object.keys(object);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label}: unknown or missing fields`);
  }
  return object;
}

const STABLE_ID_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/u;
const BOUNDED_WIRE_ID_PATTERN = /^[A-Za-z0-9_-]+$/u;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;
const BOOTSTRAP_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const UNSAFE_TEXT_PATTERN = /[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/u;
const UNPAIRED_SURROGATE_PATTERN = /[\uD800-\uDFFF]/u;

export function parseChallengeTrackId(value: unknown, label = 'trackId'): string {
  if (typeof value !== 'string' || value.length > 128 || !STABLE_ID_PATTERN.test(value)) {
    throw new Error(`${label}: expected stable lowercase id`);
  }
  return value;
}

export function parseChallengeOperationId(value: unknown): string {
  if (typeof value !== 'string' || !UUID_V4_PATTERN.test(value)) {
    throw new Error('operationId: expected canonical lowercase UUID v4');
  }
  return value;
}

export function parseChallengeBootstrapToken(value: unknown): string {
  if (typeof value !== 'string' || !BOOTSTRAP_TOKEN_PATTERN.test(value)) {
    throw new Error('bootstrapToken: expected 43-character base64url bearer');
  }
  return value;
}

function boundedText(value: unknown, label: string, maximum: number): string {
  if (typeof value !== 'string'
    || value !== value.normalize('NFKC').trim()
    || [...value].length < 1
    || [...value].length > maximum
    || UNSAFE_TEXT_PATTERN.test(value)
    || UNPAIRED_SURROGATE_PATTERN.test(value)) {
    throw new Error(`${label}: expected normalized safe text`);
  }
  return value;
}

function positiveSafeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new Error(`${label}: expected positive safe integer`);
  }
  return value as number;
}

function parseStage(value: unknown, index: number): ChallengeStageDto {
  const object = strictObject(
    value,
    ['stage', 'difficulty', 'title', 'lesson', 'passiveId', 'cardIds', 'status', 'savedDeckId'],
    `stage[${index}]`,
  );
  const stage = object.stage;
  const expectedStage = (index + 1) as ChallengeStageNumber;
  if (stage !== expectedStage) throw new Error(`stage[${index}]: invalid order`);
  const expectedDifficulty: ChallengeDifficulty = ['beginner', 'casual', 'hard'][index] as ChallengeDifficulty;
  if (object.difficulty !== expectedDifficulty) throw new Error(`stage[${index}]: invalid difficulty`);
  if (object.status !== 'locked' && object.status !== 'available' && object.status !== 'completed') {
    throw new Error(`stage[${index}]: invalid status`);
  }
  if (object.passiveId !== null
    && (typeof object.passiveId !== 'string' || !STABLE_ID_PATTERN.test(object.passiveId))) {
    throw new Error(`stage[${index}].passiveId: invalid id`);
  }
  if (!Array.isArray(object.cardIds) || object.cardIds.length !== 12) {
    throw new Error(`stage[${index}].cardIds: expected 12 cards`);
  }
  const cardIds = object.cardIds.map((cardId, cardIndex) =>
    parseChallengeTrackId(cardId, `stage[${index}].cardIds[${cardIndex}]`) as CardDefId);
  if (new Set(cardIds).size !== cardIds.length) {
    throw new Error(`stage[${index}].cardIds: duplicate cards`);
  }
  return Object.freeze({
    stage: expectedStage,
    difficulty: expectedDifficulty,
    title: boundedText(object.title, `stage[${index}].title`, 80),
    lesson: boundedText(object.lesson, `stage[${index}].lesson`, 240),
    passiveId: object.passiveId as DeckPassiveId | null,
    cardIds: Object.freeze(cardIds),
    status: object.status,
    savedDeckId: object.savedDeckId === null
      ? null
      : parseDeckUuid(object.savedDeckId, `stage[${index}].savedDeckId`),
  });
}

function parseReward(value: unknown): ChallengeRewardDto {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('reward: expected object');
  }
  const status = (value as JsonObject).status;
  if (status === 'locked') {
    strictObject(value, ['status'], 'reward');
    return Object.freeze({ status });
  }
  if (status === 'unlocked') {
    const object = strictObject(value, ['status', 'cardDefId'], 'reward');
    return Object.freeze({
      status,
      cardDefId: parseChallengeTrackId(object.cardDefId, 'reward.cardDefId') as CardDefId,
    });
  }
  throw new Error('reward: invalid status');
}

function validateStageProgress(stages: readonly ChallengeStageDto[], completedStages: number): void {
  const completed = stages.filter((stage) => stage.status === 'completed').length;
  if (completed !== completedStages) throw new Error('completedStages: inconsistent with stages');
  for (let index = 0; index < stages.length; index += 1) {
    const expected = index < completed
      ? 'completed'
      : index === completed
        ? 'available'
        : 'locked';
    if (stages[index]?.status !== expected && !(completed === 3 && stages[index]?.status === 'completed')) {
      throw new Error('stages: non-sequential progress');
    }
  }
}

export function parseChallengeTrack(value: unknown): ChallengeTrackDto {
  const object = strictObject(value, [
    'id', 'title', 'description', 'gamePlan', 'completedStages', 'stages', 'reward',
  ], 'challenge track');
  if (!Array.isArray(object.gamePlan) || object.gamePlan.length < 2 || object.gamePlan.length > 3) {
    throw new Error('gamePlan: expected 2..3 items');
  }
  if (!Array.isArray(object.stages) || object.stages.length !== 3) {
    throw new Error('stages: expected exactly three stages');
  }
  const completedStages = object.completedStages;
  if (!Number.isSafeInteger(completedStages) || (completedStages as number) < 0
    || (completedStages as number) > 3) {
    throw new Error('completedStages: expected integer 0..3');
  }
  const stages = Object.freeze(object.stages.map(parseStage));
  validateStageProgress(stages, completedStages as number);
  const reward = parseReward(object.reward);
  if ((reward.status === 'unlocked') !== (completedStages === 3)) {
    throw new Error('reward: inconsistent with track completion');
  }
  return Object.freeze({
    id: parseChallengeTrackId(object.id),
    title: boundedText(object.title, 'title', 80),
    description: boundedText(object.description, 'description', 240),
    gamePlan: Object.freeze(object.gamePlan.map((item, index) =>
      boundedText(item, `gamePlan[${index}]`, 160))),
    completedStages: completedStages as number,
    stages,
    reward,
  });
}

export function parseChallengesProjection(value: unknown): ChallengesProjection {
  const object = strictObject(value, ['catalogVersion', 'tracks'], 'challenges projection');
  if (!Array.isArray(object.tracks) || object.tracks.length > 32) {
    throw new Error('tracks: expected bounded array');
  }
  const tracks = Object.freeze(object.tracks.map(parseChallengeTrack));
  const ids = new Set(tracks.map((track) => track.id));
  if (ids.size !== tracks.length) throw new Error('tracks: duplicate id');
  return Object.freeze({
    catalogVersion: positiveSafeInteger(object.catalogVersion, 'catalogVersion'),
    tracks,
  });
}

export function parseChallengeDeckCopyResult(value: unknown): ChallengeDeckCopyResult {
  const object = strictObject(value, ['deck', 'outcome'], 'challenge deck copy result');
  if (object.outcome !== 'created' && object.outcome !== 'reused' && object.outcome !== 'replayed') {
    throw new Error('challenge deck copy outcome: unknown value');
  }
  return Object.freeze({
    deck: parseSavedDeckDto(object.deck),
    outcome: object.outcome,
  });
}

function boundedWireId(value: unknown, label: string, maximum: number): string {
  if (typeof value !== 'string' || value.length > maximum || !BOUNDED_WIRE_ID_PATTERN.test(value)) {
    throw new Error(`${label}: invalid wire id`);
  }
  return value;
}

export function parseChallengeStartResult(value: unknown): ChallengeStartResult {
  const object = strictObject(
    value,
    ['kind', 'roomId', 'playerId', 'reconnectToken'],
    'challenge start',
  );
  if (object.kind !== 'created' && object.kind !== 'replayed') {
    throw new Error('challenge start: invalid kind');
  }
  return Object.freeze({
    kind: object.kind,
    roomId: boundedWireId(object.roomId, 'roomId', 128),
    playerId: boundedWireId(object.playerId, 'playerId', 64),
    reconnectToken: parseChallengeBootstrapToken(object.reconnectToken),
  });
}
